import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { type DomainMessages, useDomainMessages } from 'domains/collections/utils/domain/intl'
import { useCollectionsFormOnSubmit } from 'domains/collections/components/collections-form/utils/collectionsFormOnSubmit.hooks'
import {
  type CollectionsFormData,
  type CollectionsFormModeProps,
} from 'domains/collections/components/collections-form/utils/collectionsForm.types'
import { type CollectionsContentType } from 'domains/collections/components/collections-form/components/content/CollectionsContent'
import { useCollectionsFormCustomValidation } from 'domains/collections/components/collections-form/utils/collectionsFormCustomValidation.hooks'

interface UseCollectionsFormProps extends CollectionsFormModeProps {
  initialValues: CollectionsFormData
  contentType: CollectionsContentType
}

export const useCollectionsForm = ({
  formModeDetails,
  initialValues,
  contentType,
}: UseCollectionsFormProps) => {
  const { submitForm, formSubmitSuccess, formSubmitLoading, formSubmitError } =
    useCollectionsFormOnSubmit({ formModeDetails, contentType })

  const form = useForm<CollectionsFormData>({
    defaultValues: initialValues,
  })
  const { register, handleSubmit, errors, getValues, setValue, control, watch } = form

  useCollectionsFormCustomValidation({ register, getValues })

  const onSubmit = handleSubmit(formData => {
    submitForm(formData)
  })

  const formErrorMessageId: DomainMessages | undefined = useMemo(() => {
    if (!formSubmitError) return undefined

    return 'collectionsDomain.collectionsForm.form.error.common'
  }, [formSubmitError])

  const messages = useDomainMessages({
    submitButton: 'collectionsDomain.collectionsForm.form.submit',
  })

  return {
    watch,
    messages,
    onSubmit,
    errors,
    register,
    getValues,
    setValue,
    control,
    formSubmitSuccess,
    formSubmitLoading,
    disableFormFields: formSubmitSuccess || formSubmitLoading,
    formErrorMessageId,
    _setValue: setValue,
  }
}
